import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { AlertTriangle, Banknote, CheckCircle2, X } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useUsers, type AdminUser } from '@/features/admin/api'
import { useMe } from '@/features/auth/api'
import { dateTime, inrFull, num, relativeTime } from '@/lib/format'
import { useLabel } from '@/lib/labels'

import {
  useCashLimitRequests,
  useConfirmLot,
  useRejectLot,
  useRejectReceipt,
  useDecideCashLimit,
  useDepositSlip,
  useLots,
  usePreviousLots,
  useReceiptsInLot,
  type CashLimitRequest,
  type LotRow,
  type LotStatus,
} from './api'

/**
 * Branch-ops cash confirmation desk. Routes mounted at /cash.
 *
 * <p>The flow: agents close lots into DEPOSITED at the bank counter; a
 * different branch-ops user (two-person integrity, enforced server-side)
 * reconciles the deposit slip against the reported amount and either
 * confirms a match (CONFIRMED) or files a mismatch (DISCREPANCY). A
 * DEPOSITED or DISCREPANCY lot can also be rejected whole (REJECTED): its
 * receipts go back to the agent to resubmit in a new lot.</p>
 *
 * <p>Layout mirrors the dashboard's Unallocated card: a header strip with
 * the count, a queue table, then a "Recent" history section underneath.</p>
 */
export function CashConfirmPage() {
  const queue = useLots('DEPOSITED', 100)
  const confirmed = useLots('CONFIRMED', 100)
  const discrepancies = useLots('DISCREPANCY', 100)
  const cancelled = useLots('CANCELLED', 100)
  const rejected = useLots('REJECTED', 100)

  // The agent userId on a LotRow is opaque — branch-ops thinks in empId.
  // The /v1/users list is small (single-tenant) so we hydrate eagerly and
  // pass a lookup map down.
  const users = useUsers()
  const empById = useMemo(() => {
    const m = new Map<string, AdminUser>()
    for (const u of users.data ?? []) m.set(u.userId, u)
    return m
  }, [users.data])

  const [activeLotId, setActiveLotId] = useState<string | null>(null)
  const [tab, setTab] = useState<'queue' | 'recent'>('queue')

  // A DEPOSITED lot opens to confirm; a DISCREPANCY lot opens to review and,
  // if need be, reject. Once either leaves its list the drawer closes.
  const activeLot =
    activeLotId
      ? [...(queue.data ?? []), ...(discrepancies.data ?? [])].find((l) => l.lotId === activeLotId) ?? null
      : null

  // Merge CONFIRMED + DISCREPANCY + CANCELLED + REJECTED for the recent strip,
  // newest first. A cancelled or rejected lot was never confirmed; it sorts by
  // when it was cancelled or rejected.
  const recent = useMemo(() => {
    const merged: LotRow[] = [
      ...(confirmed.data ?? []),
      ...(discrepancies.data ?? []),
      ...(cancelled.data ?? []),
      ...(rejected.data ?? []),
    ]
    merged.sort((a, b) => {
      const wa = recentAt(a)
      const wb = recentAt(b)
      const ta = wa ? Date.parse(wa) : 0
      const tb = wb ? Date.parse(wb) : 0
      return tb - ta
    })
    return merged
  }, [confirmed.data, discrepancies.data, cancelled.data, rejected.data])

  const queueCount = queue.data?.length ?? 0
  const totalToConfirm = (queue.data ?? []).reduce(
    (acc, l) => acc + Number(l.reportedAmount ?? 0),
    0,
  )

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Branch operations
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <Banknote className="h-5 w-5" /> Cash confirmation
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Agents close cash lots at the bank counter. A different branch-ops
          user reconciles the deposit slip against the reported amount.
          Two-person integrity is enforced — you cannot confirm a lot you
          deposited yourself.
        </p>
      </div>

      {/* Header strip — mirrors the Unallocated card pattern. */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Awaiting confirmation</CardTitle>
              <CardDescription>
                Deposited lots queued for branch-ops reconciliation.
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold tabular-nums text-navy-900">
                {num(queueCount)}
              </div>
              <div className="text-2xs uppercase tracking-wider text-muted-foreground">
                {inrFull(totalToConfirm)} reported
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
            <Chip active={tab === 'queue'} onClick={() => setTab('queue')} count={queueCount}>
              Queue
            </Chip>
            <Chip
              active={tab === 'recent'}
              onClick={() => setTab('recent')}
              count={recent.length}
            >
              Recent
            </Chip>
          </div>
          {tab === 'queue' ? (
            <QueueTable
              rows={queue.data}
              loading={queue.isPending}
              error={queue.isError}
              empById={empById}
              onConfirm={(lotId) => setActiveLotId(lotId)}
            />
          ) : (
            <RecentTable
              rows={recent}
              loading={confirmed.isPending || discrepancies.isPending || rejected.isPending}
              empById={empById}
              onReview={(lotId) => setActiveLotId(lotId)}
            />
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            CASH_DEPOSIT_CONFIRM · two-person integrity · per-tenant
          </span>
        </CardFooter>
      </Card>

      <CashLimitRequestsCard />

      {activeLot && (
        <ConfirmDrawer key={`${activeLot.lotId}:${activeLot.reportedAmount}`}
          lot={activeLot}
          empById={empById}
          onClose={() => setActiveLotId(null)}
        />
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Cash limit requests — an agent in the field asking for room (V35).
// ---------------------------------------------------------------------------

/**
 * Hidden entirely for a user without CASH_CEILING_OVERRIDE: the endpoint
 * answers 403 and an empty card would imply there is nothing waiting, which
 * is a different statement from "this is not yours to decide".
 */
function CashLimitRequestsCard() {
  const open = useCashLimitRequests('open')
  if (open.isError || open.isPending) return null
  const rows = open.data ?? []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Cash limit requests</CardTitle>
            <CardDescription>
              An agent has hit their cash limit with a customer in front of
              them. Approving raises that agent's limit; their collection then
              goes through on the next try.
            </CardDescription>
          </div>
          <div className="text-2xl font-semibold tabular-nums text-navy-900">
            {num(rows.length)}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Nothing waiting.
          </p>
        ) : (
          rows.map((r) => <CashLimitRequestItem key={r.requestId} request={r} />)
        )}
      </CardContent>
      <CardFooter>
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          CASH_CEILING_OVERRIDE · you cannot approve your own
        </span>
      </CardFooter>
    </Card>
  )
}

function CashLimitRequestItem({ request }: { request: CashLimitRequest }) {
  const [reason, setReason] = useState('')
  const [untilToday, setUntilToday] = useState(true)
  const [needReason, setNeedReason] = useState(false)
  const decide = useDecideCashLimit()

  // "Until tonight" is the decision a manager actually makes — a market day,
  // not a permanent raise. Midnight local, sent as an instant.
  function endOfToday() {
    const d = new Date()
    d.setHours(23, 59, 59, 0)
    return d.toISOString()
  }

  function act(decision: 'approve' | 'reject') {
    // Rejecting needs a reason the agent can act on. The button used to sit
    // disabled until one was typed, which read as "rejecting isn't possible".
    if (decision === 'reject' && !reason.trim()) {
      setNeedReason(true)
      return
    }
    decide.mutate({
      requestId: request.requestId,
      decision,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      ...(decision === 'approve' && untilToday ? { grantedUntil: endOfToday() } : {}),
    })
  }

  return (
    <div className="rounded-md border border-slate-200 bg-card p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-medium text-navy-900">{request.agentName}</span>{' '}
          <span className="font-mono text-2xs text-muted-foreground">{request.agentEmpId}</span>
        </div>
        <span className="text-2xs text-muted-foreground">{relativeTime(request.requestedAt)}</span>
      </div>
      <p className="mt-1 text-sm text-navy-900">{request.reason}</p>
      <div className="mt-2 flex flex-wrap gap-4 text-2xs uppercase tracking-wider text-muted-foreground">
        <span>Holding {inrFull(Number(request.exposureAtRequest))}</span>
        {request.blockedAmount && <span>Blocked on {inrFull(Number(request.blockedAmount))}</span>}
        <span>Asking for {inrFull(Number(request.requestedLimitInr))}</span>
      </div>
      <Textarea
        className="mt-2"
        rows={2}
        placeholder="Reason (required to reject)"
        value={reason}
        onChange={(e) => { setReason(e.target.value); setNeedReason(false) }}
      />
      {needReason && (
        <p className="mt-1 text-xs text-negative">Add a reason to reject — the agent needs to know why.</p>
      )}
      <label className="mt-2 flex items-center gap-2 text-2xs text-muted-foreground">
        <input
          type="checkbox"
          className="h-4 w-4 accent-navy-700"
          checked={untilToday}
          onChange={(e) => setUntilToday(e.target.checked)}
        />
        Only until midnight tonight
      </label>
      {decide.isError && (
        <p className="mt-2 text-xs text-negative">
          {isAxiosError(decide.error)
            ? String(
                (decide.error.response?.data as { message?: string } | undefined)?.message ??
                  'Could not record that decision.',
              )
            : 'Could not record that decision.'}
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <Button type="button" onClick={() => act('approve')} disabled={decide.isPending}>
          Approve
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => act('reject')}
          disabled={decide.isPending}
        >
          Reject
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Queue table — DEPOSITED rows awaiting branch-ops.
// ---------------------------------------------------------------------------

function QueueTable({
  rows,
  loading,
  error,
  empById,
  onConfirm,
}: {
  rows: LotRow[] | undefined
  loading: boolean
  error: boolean
  empById: Map<string, AdminUser>
  onConfirm: (lotId: string) => void
}) {
  if (loading) return <SkeletonRows rows={5} cols={5} />
  if (error) {
    return (
      <div className="px-5 pb-4 text-sm text-negative">
        Couldn't load lots — CASH_DEPOSIT_CONFIRM is required.
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm font-medium text-navy-900">Nothing to confirm</p>
        <p className="mt-1 text-xs text-muted-foreground">
          When an agent deposits a lot at the bank counter, it lands here.
        </p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
          <tr>
            <th className="px-5 py-2 font-medium">Agent</th>
            <th className="px-3 py-2 text-right font-medium">Reported</th>
            <th className="px-3 py-2 font-medium">Reference</th>
            <th className="px-3 py-2 font-medium">Deposited</th>
            <th className="px-5 py-2 text-right font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((lot) => {
            const agent = empById.get(lot.agentUserId)
            return (
              <tr
                key={lot.lotId}
                className="border-b border-slate-200 last:border-0 transition-colors hover:bg-navy-50"
              >
                <td className="px-5 py-2 font-mono text-xs text-navy-900">
                  {agent?.empId ?? (
                    <span className="text-muted-foreground" title={lot.agentUserId}>
                      {lot.agentUserId.slice(0, 8)}…
                    </span>
                  )}
                  {agent?.fullName && (
                    <span className="ml-2 font-sans text-2xs text-muted-foreground">
                      {agent.fullName}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-navy-900">
                  {inrFull(lot.reportedAmount)}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-navy-900">
                  {lot.depositReference ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground" title={dateTime(lot.depositedAt)}>
                  {relativeTime(lot.depositedAt)}
                </td>
                <td className="px-5 py-2 text-right">
                  <Button size="sm" onClick={() => onConfirm(lot.lotId)}>
                    Confirm
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recent — CONFIRMED + DISCREPANCY + CANCELLED + REJECTED history.
// ---------------------------------------------------------------------------

/**
 * When a recent lot was last acted on: confirmed, cancelled by rejecting every
 * receipt, or rejected whole. A rejected DISCREPANCY lot also has a
 * confirmedAt, so the rejection wins.
 */
function recentAt(lot: LotRow): string | null {
  return lot.rejectedAt ?? lot.confirmedAt ?? lot.cancelledAt ?? null
}

function RecentTable({
  rows,
  loading,
  empById,
  onReview,
}: {
  rows: LotRow[]
  loading: boolean
  empById: Map<string, AdminUser>
  onReview: (lotId: string) => void
}) {
  if (loading) return <SkeletonRows rows={5} cols={6} />
  if (rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm font-medium text-navy-900">No recent activity</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Confirmed, discrepancy, cancelled and rejected lots will show here as they're processed.
        </p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
          <tr>
            <th className="px-5 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Agent</th>
            <th className="px-3 py-2 text-right font-medium">Reported</th>
            <th className="px-3 py-2 text-right font-medium">Confirmed</th>
            <th className="px-3 py-2 font-medium">Performed by</th>
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-5 py-2 text-right font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((lot) => {
            const agent = empById.get(lot.agentUserId)
            const isDiscrepancy = lot.status === 'DISCREPANCY'
            const isCancelled = lot.status === 'CANCELLED'
            const isRejected = lot.status === 'REJECTED'
            // Who settled the lot: the confirmer, whoever rejected its last
            // receipt, or whoever rejected the whole lot.
            const performerId = isRejected
              ? lot.rejectedByUserId
              : isCancelled ? lot.cancelledByUserId : lot.confirmedByUserId
            const performer = performerId ? empById.get(performerId) : undefined
            const when = recentAt(lot)
            return (
              <tr
                key={lot.lotId}
                className={
                  'border-b border-slate-200 last:border-0 ' +
                  (isDiscrepancy ? 'bg-negative-soft/50' : '')
                }
              >
                <td className="px-5 py-2">
                  <Badge tone={isDiscrepancy || isCancelled || isRejected ? 'negative' : 'positive'}>
                    {lot.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-navy-900">
                  {agent?.empId ?? (
                    <span className="text-muted-foreground">
                      {lot.agentUserId.slice(0, 8)}…
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-navy-900">
                  {/* A fully rejected lot's reportedAmount is 0; show what was reported. */}
                  {inrFull(isCancelled ? lot.originalReportedAmount ?? lot.reportedAmount : lot.reportedAmount)}
                </td>
                <td
                  className={
                    'px-3 py-2 text-right tabular-nums ' +
                    (isDiscrepancy ? 'text-negative font-medium' : 'text-navy-900')
                  }
                >
                  {/* Every receipt was rejected, so accounts received nothing. A
                      rejected lot keeps whatever was counted before it went back. */}
                  {isRejected && !lot.confirmedAmount
                    ? <span className="text-muted-foreground">—</span>
                    : inrFull(isCancelled ? lot.confirmedAmount ?? '0' : lot.confirmedAmount)}
                </td>
                <td
                  className="px-3 py-2 font-mono text-xs text-navy-900"
                  title={
                    isRejected
                      ? `Rejected the lot: ${lot.rejectionReason ?? ''}`
                      : isCancelled ? 'Rejected all receipts in the lot' : undefined
                  }
                >
                  {performer?.empId ?? <span className="text-muted-foreground">—</span>}
                  {isRejected && lot.rejectionReason && (
                    <span className="block font-sans text-2xs text-muted-foreground">{lot.rejectionReason}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground" title={dateTime(when)}>
                  {relativeTime(when)}
                </td>
                <td className="px-5 py-2 text-right">
                  {isDiscrepancy && (
                    <Button size="sm" variant="outline" onClick={() => onReview(lot.lotId)}>
                      Review
                    </Button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Confirm drawer — slide-over with the lot detail + amount input.
// ---------------------------------------------------------------------------

function ConfirmDrawer({
  lot,
  empById,
  onClose,
}: {
  lot: LotRow
  empById: Map<string, AdminUser>
  onClose: () => void
}) {
  const confirm = useConfirmLot()
  const can = navPermissionCheck(useMe().data)
  // A DISCREPANCY lot has been counted already: it can only be reviewed and rejected.
  const reviewOnly = lot.status !== 'DEPOSITED'
  const [rejecting, setRejecting] = useState(false)
  const [rejectingLot, setRejectingLot] = useState(false)
  const [amount, setAmount] = useState<string>(lot.reportedAmount)
  const [note, setNote] = useState('')

  // Reset when the active lot changes — drawer is reused across rows.
  useEffect(() => {
    setAmount(lot.reportedAmount)
    setNote('')
    confirm.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot.lotId])

  // Esc to close. Backdrop click too.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const agent = empById.get(lot.agentUserId)
  const parsed = Number(amount)
  const validAmount = Number.isFinite(parsed) && parsed >= 0
  const matches = validAmount && Math.abs(parsed - Number(lot.reportedAmount)) < 0.005
  const needsNote = validAmount && !matches // discrepancy — note is "optional" but strongly nudged

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validAmount || confirm.isPending) return
    confirm.mutate(
      { lotId: lot.lotId, confirmedAmount: parsed, note },
      {
        onSuccess: () => {
          // Keep the drawer open briefly so the user sees the outcome,
          // then close. The list invalidation refreshes the queue.
          setTimeout(onClose, 1200)
        },
      },
    )
  }

  const errorMsg = confirm.isError ? lotActionError(confirm.error, 'Confirm', 'CASH_DEPOSIT_CONFIRM') : null
  const title = reviewOnly ? 'Review discrepancy lot' : 'Confirm cash lot'

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-navy-900/50 backdrop-blur-sm transition-opacity duration-fluid ease-fluid"
      />
      <aside
        className="flex h-full w-full max-w-[min(560px,95vw)] flex-col bg-navy-50 shadow-lift animate-slide-in-right"
        style={{ animationDuration: '180ms' }}
      >
        <div className="flex h-13 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
            {title}
          </span>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
            Close
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Card>
            <CardHeader>
              <CardTitle>Lot detail</CardTitle>
              <CardDescription>Verify against the bank deposit slip.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Lot ID">
                <span className="break-all font-mono text-xs text-navy-900">{lot.lotId}</span>
              </DetailRow>
              <DetailRow label="Agent">
                <span className="font-mono text-xs text-navy-900">
                  {agent?.empId ?? lot.agentUserId.slice(0, 8) + '…'}
                </span>
                {agent?.fullName && (
                  <span className="ml-2 text-2xs text-muted-foreground">{agent.fullName}</span>
                )}
              </DetailRow>
              <DetailRow label="Reported amount">
                <span className="font-semibold tabular-nums text-navy-900">
                  {inrFull(lot.reportedAmount)}
                </span>
              </DetailRow>
              <DetailRow label="Deposit reference">
                <span className="font-mono text-xs text-navy-900">
                  {lot.depositReference ?? '—'}
                </span>
              </DetailRow>
              <DetailRow label="Deposited into">
                <span className="text-navy-900">
                  {lot.depositBankLabel ?? 'Not recorded'}
                </span>
              </DetailRow>
              <DetailRow label="Deposited at">
                <span className="text-navy-900">{dateTime(lot.depositedAt)}</span>
              </DetailRow>
              <DetailRow label="Opened at">
                <span className="text-muted-foreground">{dateTime(lot.openedAt)}</span>
              </DetailRow>
              {reviewOnly && (
                <>
                  <DetailRow label="Counted amount">
                    <span className="font-semibold tabular-nums text-negative">
                      {inrFull(lot.confirmedAmount)}
                    </span>
                  </DetailRow>
                  <DetailRow label="Discrepancy note">
                    <span className="text-navy-900">{lot.discrepancyNote || '—'}</span>
                  </DetailRow>
                </>
              )}
            </CardContent>
          </Card>

          <DepositSlipCard lot={lot} />

          <PreviousLotsCard lotId={lot.lotId} empById={empById} />

          {/* C13: full receipt list inside the lot. Branch ops sees
              exactly which receipts they're confirming — much easier to
              spot a stray receipt here than after it lands in
              DISCREPANCY. */}
          <LotReceiptsCard
            lotId={lot.lotId}
            reportedAmount={lot.reportedAmount}
            onRejecting={setRejecting}
            confirming={confirm.isPending || rejectingLot}
            allowReject={!reviewOnly}
          />

          {can('CASH_LOT_REJECT') && (
            <RejectLotCard
              lot={lot}
              blocked={confirm.isPending || confirm.isSuccess || rejecting}
              onRejecting={setRejectingLot}
              onRejected={onClose}
            />
          )}

          {!reviewOnly && <form onSubmit={onSubmit} className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Confirmation</CardTitle>
                <CardDescription>
                  Enter the amount as it appears on the bank slip. A mismatch
                  flags the lot as DISCREPANCY for follow-up.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="confirmed-amount">Confirmed amount (INR)</Label>
                  <Input
                    id="confirmed-amount"
                    inputMode="decimal"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="tabular-nums"
                    required
                  />
                  {validAmount && !matches && (
                    <p className="mt-1 flex items-center gap-1 text-2xs uppercase tracking-wider text-warning">
                      <AlertTriangle className="h-3 w-3" />
                      Differs from reported · this will be a DISCREPANCY
                    </p>
                  )}
                  {validAmount && matches && (
                    <p className="mt-1 text-2xs uppercase tracking-wider text-positive">
                      Matches reported amount
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="confirm-note">
                    Note {needsNote ? '' : <span className="text-muted-foreground">(optional)</span>}
                  </Label>
                  <Textarea
                    id="confirm-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={
                      needsNote
                        ? 'Describe the discrepancy — short count, denomination dispute, etc.'
                        : 'Anything worth recording for the audit chain.'
                    }
                    rows={3}
                  />
                </div>

                {errorMsg && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Could not confirm</AlertTitle>
                    <AlertDescription>{errorMsg}</AlertDescription>
                  </Alert>
                )}
                {confirm.isSuccess && (
                  <Alert variant={confirm.data.amountsMatched ? 'positive' : 'warning'}>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>
                      {confirm.data.amountsMatched ? 'Confirmed' : 'Recorded as discrepancy'}
                    </AlertTitle>
                    <AlertDescription>
                      Lot is now {confirm.data.newStatus}. Reported {inrFull(confirm.data.reportedAmount)},
                      confirmed {inrFull(confirm.data.confirmedAmount)}.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
              <CardFooter>
                <span className="text-2xs uppercase tracking-wider text-muted-foreground">
                  Logged to the audit chain
                </span>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!validAmount || confirm.isPending || confirm.isSuccess || rejecting || rejectingLot}
                  >
                    {confirm.isPending ? 'Confirming…' : matches ? 'Confirm match' : 'Record discrepancy'}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </form>}
        </div>
      </aside>
    </div>
  )
}

/**
 * The bank deposit slip the agent photographed when depositing — what the
 * confirmer reads the amount off. Sits right under the lot detail so the
 * reported figure and the slip can be compared at a glance.
 */
function DepositSlipCard({ lot }: { lot: LotRow }) {
  // Lots from an API that predates the flag leave it undefined: try the fetch.
  const hasSlip = lot.hasDepositSlip !== false
  const { data, isPending, error } = useDepositSlip(lot.lotId, hasSlip)
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!data) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(data.blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [data])

  const status = isAxiosError(error) ? error.response?.status : undefined
  const type = data?.blob.type ?? ''
  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Deposit slip</CardTitle>
            <CardDescription>Uploaded by the agent at the bank counter.</CardDescription>
          </div>
          {data && !data.verified && (
            <Badge tone="negative" title="The stored slip does not hash to the sha256 recorded when it was uploaded.">
              <AlertTriangle className="h-3 w-3" />
              Integrity check failed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {(!hasSlip || status === 404) && (
          <p className="text-xs text-muted-foreground">
            No slip uploaded for this lot. Check the deposit reference with the bank before confirming.
          </p>
        )}
        {hasSlip && isPending && <div className="h-48 w-full animate-pulse rounded bg-slate-100" />}
        {status === 403 && (
          <p className="text-xs text-negative">
            This lot belongs to an agent outside your branch in the org hierarchy, so its slip is not shown.
          </p>
        )}
        {error && status !== 403 && status !== 404 && (
          <p className="text-xs text-negative">Couldn't load the slip. Try reopening the lot.</p>
        )}
        {url && type.startsWith('image/') && (
          <a href={url} target="_blank" rel="noreferrer" title="Open full size" className="block">
            <img
              src={url}
              alt={`Deposit slip for lot ${lot.lotId}`}
              className="max-h-80 w-full rounded border border-slate-200 bg-white object-contain"
            />
          </a>
        )}
        {url && type === 'application/pdf' && (
          <a href={url} target="_blank" rel="noreferrer" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            Open slip (PDF)
          </a>
        )}
        {url && !type.startsWith('image/') && type !== 'application/pdf' && (
          <a href={url} download={`deposit-slip-${lot.lotId}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            Download slip
          </a>
        )}
      </CardContent>
    </Card>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] items-baseline gap-3">
      <span className="text-2xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  )
}

/**
 * Receipt review and rejection for a pending lot. Renders inside the confirm drawer so
 * branch ops sees exactly the receipts that compose the reported amount —
 * a stray receipt or a bad payment_mode is much easier to catch HERE
 * than after the lot lands in DISCREPANCY. Renders a sum-vs-reported
 * sanity check at the foot so the user has a numeric cross-check.
 */
function LotReceiptsCard({
  lotId,
  reportedAmount,
  onRejecting,
  confirming,
  allowReject,
}: {
  onRejecting: (value: boolean) => void
  confirming: boolean
  lotId: string
  reportedAmount: string
  /** Receipts can only be rejected one by one while the lot is DEPOSITED. */
  allowReject: boolean
}) {
  const label = useLabel()
  const { data, isPending, isError } = useReceiptsInLot(lotId)
  const reject = useRejectReceipt()
  const [selected, setSelected] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  function select(id: string | null) {
    setSelected(id)
    setReason('')
    reject.reset()
    onRejecting(id !== null)
  }
  const sum =
    data?.reduce(
      (a, r) => a + (r.cancelledAt ? 0 : Number(r.amount) || 0),
      0,
    ) ?? 0
  const reported = Number(reportedAmount) || 0
  const sumMatches = Math.abs(sum - reported) < 0.005
  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Receipts in this lot</CardTitle>
            <CardDescription>
              {isPending ? '—' : `${data?.length ?? 0} receipt${data?.length === 1 ? '' : 's'}`}
            </CardDescription>
          </div>
          {data && (
            <span
              className={
                'inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider tabular-nums ' +
                (sumMatches
                  ? 'bg-positive-soft text-positive'
                  : 'bg-negative-soft text-negative')
              }
              title={`Sum-of-receipts ${inrFull(sum)} vs reported ${inrFull(reported)}`}
            >
              Σ {inrFull(sum)}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {isPending && (
          <div className="space-y-1 px-5 pb-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-7 w-full rounded bg-slate-100" />
            ))}
          </div>
        )}
        {isError && (
          <p className="px-5 pb-4 text-xs text-negative">
            Couldn't load the receipts. The confirm still works — but you
            won't see the per-receipt breakdown.
          </p>
        )}
        {data && data.length === 0 && (
          <p className="px-5 pb-4 text-xs text-muted-foreground">
            No receipts found in this lot. That's unusual — refuse the
            confirm and ask ops to cancel the lot.
          </p>
        )}
        {selected && (
          <form className="m-4 space-y-3 rounded border border-red-300 p-3" role="alertdialog" aria-label="Reject receipt"
            onSubmit={(event) => {
              event.preventDefault()
              if (!reason.trim() || reject.isPending || confirming) return
              reject.mutate({ receiptId: selected, reason }, { onSuccess: () => select(null) })
            }}>
            <p className="font-semibold">Reject receipt {data?.find(r => r.receiptId === selected)?.receiptSeq}?</p>
            <p className="text-sm">Cash has not been received by accounts. This cancels the receipt and reverses the collection. The amount becomes due again; the agent must record a fresh payment and issue a new receipt.</p>
            <Label htmlFor="rejection-reason">Reason for rejection</Label>
            <Textarea id="rejection-reason" value={reason} maxLength={500} required disabled={reject.isPending}
              onChange={e => setReason(e.target.value)} />
            {reject.isError && <p role="alert" className="text-negative text-sm">
              {isAxiosError(reject.error) ? reject.error.response?.data?.detail ?? 'Could not reject receipt. Refresh and try again.' : 'Could not reject receipt.'}
            </p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={reject.isPending} onClick={() => select(null)}>Keep receipt</Button>
              <Button type="submit" disabled={!reason.trim() || reject.isPending || confirming}>
                {reject.isPending ? 'Rejecting…' : 'Reject and cancel receipt'}
              </Button>
            </div>
          </form>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
              <tr>
                <th className="px-5 py-2 font-medium">Seq</th>
                <th className="whitespace-nowrap px-3 py-2 font-medium">Loan A/C</th>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-5 py-2 font-medium">Collected</th>
                {allowReject && <th className="px-3 py-2 font-medium">Action</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr
                  key={r.receiptId}
                  className={
                    'border-b border-slate-200 last:border-0 ' +
                    (r.cancelledAt ? 'bg-negative-soft/30' : '')
                  }
                  title={r.cancelledAt ? 'Cancelled — excluded from Σ' : undefined}
                >
                  <td className="px-5 py-1.5 font-mono text-2xs tabular-nums text-muted-foreground">
                    {r.receiptSeq}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-2xs text-navy-900">
                    {r.loanAccountNo ?? '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-navy-700">
                      {label('paymentMode', r.paymentMode)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium text-navy-900">
                    {inrFull(r.amount)}
                  </td>
                  <td className="px-5 py-1.5 text-2xs text-muted-foreground">
                    {dateTime(r.collectedAt)}
                  </td>
                  {allowReject && <td className="px-3 py-2">
                    {r.cancelledAt ? <span className="text-xs text-negative">Cancelled — cash not received by accounts<br />{r.cancelledReason}</span> : (
                      <Button variant="outline" size="sm" disabled={confirming || reject.isPending}
                        onClick={() => select(r.receiptId)}>Reject</Button>
                    )}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      <CardFooter>
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          {sumMatches
            ? 'Σ of receipts matches reported amount'
            : `Σ ${inrFull(sum)} ≠ reported ${inrFull(reported)} — investigate`}
        </span>
      </CardFooter>
    </Card>
  )
}

/** The confirm/reject error a branch-ops user can act on. */
function lotActionError(error: unknown, verb: string, permission: string): string {
  if (!isAxiosError(error)) return `${verb} failed.`
  const status = error.response?.status
  const data = error.response?.data as { code?: string; detail?: string; message?: string } | undefined
  const code = data?.code ?? ''
  const detail = data?.detail ?? data?.message ?? ''
  if (status === 409 && (code.includes('two_person_required') || detail.includes('two_person_required'))) {
    return 'Branch ops must be a different user from the depositing agent.'
  }
  // A 403 is not always a missing permission: holders of it are also
  // refused a lot whose agent sits outside their part of the org tree.
  if (status === 403 && (code.includes('agent_out_of_scope') || detail.includes('agent_out_of_scope'))) {
    return 'This lot belongs to an agent outside your branch in the org hierarchy. Ask an admin to place the agent under your branch, or to widen your role’s scope.'
  }
  if (status === 403) return `Your role does not include ${permission}.`
  return detail || `${verb} failed (HTTP ${status ?? '?'}).`
}

/**
 * Send the whole lot back to the agent — e.g. they reported ₹10,000 but the
 * bank shows ₹8,000. Nothing is cancelled: every receipt returns to the agent,
 * who deposits the shortfall and resubmits the same receipts in a new lot.
 */
function RejectLotCard({
  lot,
  blocked,
  onRejecting,
  onRejected,
}: {
  lot: LotRow
  /** A confirm or a single-receipt rejection is in progress. */
  blocked: boolean
  onRejecting: (value: boolean) => void
  onRejected: () => void
}) {
  const reject = useRejectLot()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  function toggle(value: boolean) {
    setOpen(value)
    setReason('')
    reject.reset()
    onRejecting(value)
  }
  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Reject lot</CardTitle>
            <CardDescription>
              The deposit doesn't add up? Send every receipt back to the agent to deposit the
              shortfall and resubmit.
            </CardDescription>
          </div>
          {!open && (
            <Button type="button" variant="outline" size="sm" disabled={blocked} onClick={() => toggle(true)}>
              Reject lot
            </Button>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          <form className="space-y-3 rounded border border-red-300 p-3" role="alertdialog" aria-label="Reject lot"
            onSubmit={(event) => {
              event.preventDefault()
              if (!reason.trim() || reject.isPending || blocked) return
              reject.mutate({ lotId: lot.lotId, reason }, { onSuccess: onRejected })
            }}>
            <p className="font-semibold">Reject this lot of {inrFull(lot.reportedAmount)}?</p>
            <p className="text-sm">
              Every receipt in it goes back to the agent's available receipts, unchanged — nothing is
              cancelled and the collections stay recorded. The agent deposits any shortfall and puts the
              same receipts in a new lot for you to confirm. This lot stays in Recent as REJECTED.
            </p>
            <Label htmlFor="lot-rejection-reason">Reason for rejecting the lot</Label>
            <Textarea id="lot-rejection-reason" value={reason} maxLength={500} required disabled={reject.isPending}
              placeholder="e.g. Reported ₹10,000, bank shows ₹8,000"
              onChange={(e) => setReason(e.target.value)} />
            {reject.isError && (
              <p role="alert" className="text-sm text-negative">
                {lotActionError(reject.error, 'Reject', 'CASH_LOT_REJECT')}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={reject.isPending} onClick={() => toggle(false)}>
                Keep lot
              </Button>
              <Button type="submit" disabled={!reason.trim() || reject.isPending || blocked}>
                {reject.isPending ? 'Rejecting…' : 'Reject lot and return receipts'}
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  )
}

/**
 * Earlier lots these receipts were in, which a manager rejected. The agent
 * resubmits the same receipts after depositing only the shortfall, so the
 * earlier lot's slip is half of the evidence for this one.
 */
function PreviousLotsCard({ lotId, empById }: { lotId: string; empById: Map<string, AdminUser> }) {
  const { data } = usePreviousLots(lotId)
  if (!data || data.length === 0) return null
  return (
    <Card className="mt-4 border-warning">
      <CardHeader>
        <CardTitle>Previously rejected lot{data.length === 1 ? '' : 's'}</CardTitle>
        <CardDescription>
          These receipts were submitted before and sent back. Add the earlier deposit to this one's
          slip when checking the total.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {data.map((prev) => (
          <div key={prev.lotId} className="space-y-2">
            <DetailRow label="Rejected">
              <span className="text-navy-900">{dateTime(prev.rejectedAt)}</span>
              {prev.rejectedByUserId && (
                <span className="ml-2 font-mono text-2xs text-muted-foreground">
                  {empById.get(prev.rejectedByUserId)?.empId ?? ''}
                </span>
              )}
            </DetailRow>
            <DetailRow label="Reported">
              <span className="tabular-nums text-navy-900">{inrFull(prev.reportedAmount)}</span>
            </DetailRow>
            {prev.confirmedAmount && (
              <DetailRow label="Counted">
                <span className="tabular-nums text-navy-900">{inrFull(prev.confirmedAmount)}</span>
              </DetailRow>
            )}
            <DetailRow label="Deposit reference">
              <span className="font-mono text-xs text-navy-900">{prev.depositReference ?? '—'}</span>
            </DetailRow>
            <DetailRow label="Reason">
              <span className="text-navy-900">{prev.rejectionReason ?? '—'}</span>
            </DetailRow>
            <DepositSlipCard lot={prev} />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// Keep the LotStatus import alive for tree-shakers; it's part of the public type.
export type { LotStatus }
